import {
  Box,
  Switch,
  Theme,
  TextField,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  Chip,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  adminGetPasswordConfig,
  adminUpdatePasswordConfig,
} from "./passwordConfigurationActions";
import { RootState } from "../../store/store";
import MessageDialog from "../../components/MessageDialog/MessageDialog";

interface PasswordPolicyConfig {
  minLength: { value: number };
  maxLength: { value: number };
  requireUppercase: { enabled: boolean; value: number };
  requireLowercase: { enabled: boolean; value: number };
  requireSpecialChars: {
    enabled: boolean;
    value: boolean;
    chars: string;
    length: number;
  };
  requireNumbers: { enabled: boolean; value: number };
  passwordExpiry: { enabled: boolean; value: number };
}

// Update the submit data interface to match the required format
interface PasswordPolicySubmitData {
  minimumLength: number;
  maximumLength: number;
  upperCase?: boolean;
  upperCaseValue?: number;
  lowerCase?: boolean;
  lowerCaseValue?: number;
  specialCharacters?: boolean;
  specialCharactersValue?: string;
  specialCharactersLength?: number;
  numericCharacters?: boolean;
  numericCharactersValue?: number;
  passwordExpiry?: boolean;
  passwordExpiryValue?: number;
  passwordHistory?: boolean;
  passwordHistoryValue?: number;
}

// Add an interface for validation errors
interface ValidationErrors {
  minLength?: string;
  maxLength?: string;
  requireUppercase?: string;
  requireLowercase?: string;
  requireNumbers?: string;
  passwordExpiry?: string;
  requireSpecialChars?: {
    chars?: string;
    length?: string;
  };
}

const defaultPasswordConfig: PasswordPolicyConfig = {
  minLength: { value: 8 },
  maxLength: { value: 20 },
  requireUppercase: { enabled: true, value: 1 },
  requireLowercase: { enabled: true, value: 1 },
  requireSpecialChars: {
    enabled: true,
    value: true, // This is a boolean in your config, but you may want to use chars/length for actual values
    chars: "!@#",
    length: 1,
  },
  requireNumbers: { enabled: true, value: 1 },
  passwordExpiry: { enabled: true, value: 90 },
};

const PasswordConfiguration = () => {
  const dispatch = useAppDispatch();
  const { passwordConfigData } = useAppSelector(
    (state: RootState) => state.adminPasswordConfig
  );
  const { isSuccess, calledReducerType, isError } = useAppSelector(
    (state) => state.common
  );
  // Move mappedConfig inside the component
  const getMappedConfig = (): PasswordPolicyConfig => {
    return {
      minLength: { value: passwordConfigData.minimumLength || 8 },
      maxLength: { value: passwordConfigData.maximumLength || 20 },
      requireUppercase: {
        enabled: passwordConfigData.upperCase || false,
        value: passwordConfigData.upperCaseValue || 1,
      },
      requireLowercase: {
        enabled: passwordConfigData.lowerCase || false,
        value: passwordConfigData.lowerCaseValue || 1,
      },
      requireSpecialChars: {
        enabled: passwordConfigData.specialCharacters || false,
        value: !!passwordConfigData.specialCharactersValue,
        chars: passwordConfigData.specialCharactersValue || "!@#",
        length: passwordConfigData.specialCharactersLength || 1,
      },
      requireNumbers: {
        enabled: passwordConfigData.numericCharacters || false,
        value: passwordConfigData.numericCharactersValue || 1,
      },
      passwordExpiry: {
        enabled: passwordConfigData.passwordExpiry || false,
        value: passwordConfigData.passwordExpiryValue || 90,
      },
    };
  };

  // Initialize config with mapped data or default config
  const [config, setConfig] = useState<PasswordPolicyConfig>(getMappedConfig());
  const [initialConfig, setInitialConfig] = useState<PasswordPolicyConfig>(
    getMappedConfig()
  );
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [messageDialogOpen, setMessageDialogOpen] = useState<boolean>(false);

  const handleMessageDialogClose = () => {
    setMessageDialogOpen(false);
  };
  // Update config when passwordConfigData changes
  useEffect(() => {
    const mapped = getMappedConfig();
    setConfig(mapped);
    setInitialConfig(mapped);
  }, [passwordConfigData]);

  // Fetch password config on component mount
  useEffect(() => {
    dispatch(adminGetPasswordConfig());
  }, [dispatch]);

  // Validation function
  const validateField = (field: keyof PasswordPolicyConfig, value: any) => {
    const newErrors: ValidationErrors = { ...errors };

    switch (field) {
      case "minLength":
        if (value.value < 6) {
          newErrors.minLength = "Minimum length cannot be less than 6";
        } else if (value.value > 50) {
          newErrors.minLength = "Minimum length cannot be greater than 50";
        } else {
          delete newErrors.minLength;
        }
        // Also validate maxLength when minLength changes
        if (config.maxLength.value < value.value) {
          newErrors.maxLength =
            "Maximum length must be greater than minimum length";
        } else {
          delete newErrors.maxLength;
        }
        break;

      case "maxLength":
        if (value.value < config.minLength.value) {
          newErrors.maxLength =
            "Maximum length must be greater than minimum length";
        } else if (value.value > 50) {
          newErrors.maxLength = "Maximum length cannot be greater than 50";
        } else {
          delete newErrors.maxLength;
        }
        break;

      case "requireUppercase":
        if (value.enabled && value.value <= 0) {
          newErrors.requireUppercase = "Value must be greater than 0";
        } else {
          delete newErrors.requireUppercase;
        }
        break;

      case "requireLowercase":
        if (value.enabled && value.value <= 0) {
          newErrors.requireLowercase = "Value must be greater than 0";
        } else {
          delete newErrors.requireLowercase;
        }
        break;

      case "requireNumbers":
        if (value.enabled && value.value <= 0) {
          newErrors.requireNumbers = "Value must be greater than 0";
        } else {
          delete newErrors.requireNumbers;
        }
        break;

      case "passwordExpiry":
        if (value.enabled && value.value <= 0) {
          newErrors.passwordExpiry = "Value must be greater than 0";
        } else if (
          value.enabled &&
          value.value &&
          value.value.toString().replace(/^0+/, "").length > 3
        ) {
          newErrors.passwordExpiry =
            "Digit cannot be greater than 3 places excluding zero at the start";
        } else {
          delete newErrors.passwordExpiry;
        }
        break;

      case "requireSpecialChars":
        if (value.enabled) {
          // Initialize requireSpecialChars in errors if not exists
          if (!newErrors.requireSpecialChars) {
            newErrors.requireSpecialChars = {};
          }

          // Validate special characters input
          if (value.chars) {
            // Check if input contains any alphanumeric characters
            if (value.chars.match(/[a-zA-Z0-9]/)) {
              newErrors.requireSpecialChars.chars =
                "Only special characters are allowed";
            } else if (value.chars.length === 0) {
              newErrors.requireSpecialChars.chars =
                "Special characters are required";
            } else if (new Set(value.chars).size !== value.chars.length) {
              newErrors.requireSpecialChars.chars =
                "Duplicate special characters are not allowed";
            } else {
              delete newErrors.requireSpecialChars.chars;
            }
          } else {
            newErrors.requireSpecialChars.chars =
              "Special characters are required";
          }

          // Validate length
          if (value.length <= 0) {
            newErrors.requireSpecialChars.length =
              "Length must be greater than 0";
          } else {
            delete newErrors.requireSpecialChars.length;
          }

          // Remove requireSpecialChars object if no errors
          if (
            !newErrors.requireSpecialChars.chars &&
            !newErrors.requireSpecialChars.length
          ) {
            delete newErrors.requireSpecialChars;
          }
        } else {
          delete newErrors.requireSpecialChars;
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Modified handleChange function
  const handleChange = (
    field: keyof PasswordPolicyConfig,
    type: "enabled" | "value" | "values" | "chars" | "length",
    value: any
  ) => {
    const newConfig = {
      ...config,
      [field]: {
        ...config[field],
        [type]: value,
      },
    };

    validateField(field, newConfig[field]);
    setConfig(newConfig);
  };

  const handleSave = () => {
    // Validate all fields before saving
    let isValid = true;
    Object.keys(config).forEach((key) => {
      const field = key as keyof PasswordPolicyConfig;
      if (!validateField(field, config[field])) {
        isValid = false;
      }
    });

    if (!isValid) {
      return; // Don't save if there are validation errors
    }

    const finalConfig: PasswordPolicySubmitData = {
      // Always include minimum and maximum length
      minimumLength: config.minLength.value,
      maximumLength: config.maxLength.value,
    };

    // Handle uppercase configuration
    if (config.requireUppercase.enabled) {
      finalConfig.upperCase = true;
      finalConfig.upperCaseValue = config.requireUppercase.value;
    } else {
      finalConfig.upperCase = false;
    }

    // Handle lowercase configuration
    if (config.requireLowercase.enabled) {
      finalConfig.lowerCase = true;
      finalConfig.lowerCaseValue = config.requireLowercase.value;
    } else {
      finalConfig.lowerCase = false;
    }

    // Handle special characters configuration
    if (config.requireSpecialChars.enabled) {
      finalConfig.specialCharacters = true;
      finalConfig.specialCharactersValue = config.requireSpecialChars.chars;
      finalConfig.specialCharactersLength = config.requireSpecialChars.length;
    } else {
      finalConfig.specialCharacters = false;
    }

    // Handle numeric characters configuration
    if (config.requireNumbers.enabled) {
      finalConfig.numericCharacters = true;
      finalConfig.numericCharactersValue = config.requireNumbers.value;
    } else {
      finalConfig.numericCharacters = false;
    }

    // Handle password expiry configuration
    if (config.passwordExpiry.enabled) {
      finalConfig.passwordExpiry = true;
      finalConfig.passwordExpiryValue = config.passwordExpiry.value;
    } else {
      finalConfig.passwordExpiry = false;
    }

    // Handle password history configuration
    // if (config.passwordHistory.enabled) {
    //   finalConfig.passwordHistory = true;
    //   finalConfig.passwordHistoryValue = config.passwordHistory.value;
    // } else {
    //   finalConfig.passwordHistory = false;
    // }

    dispatch(adminUpdatePasswordConfig(finalConfig));
  };

  const handleReset = () => {
    setConfig(defaultPasswordConfig);
    setErrors({});
  };

  useEffect(() => {
    if (
      (isSuccess || isError) &&
      calledReducerType === "config/adminUpdatePasswordConfig"
    ) {
      setMessageDialogOpen(true);
    }
  }, [isSuccess, isError, calledReducerType]);

  useEffect(() => {
    if (isSuccess && calledReducerType === "config/adminUpdatePasswordConfig") {
      setInitialConfig(JSON.parse(JSON.stringify(config)));
    }
  }, [isSuccess, calledReducerType]);

  const isConfigEqual = (a: PasswordPolicyConfig, b: PasswordPolicyConfig) => {
    return JSON.stringify(a) === JSON.stringify(b);
  };

  const isFormValid = () => Object.keys(errors).length === 0;

  const isConfigDefault = (a: PasswordPolicyConfig) => {
    return JSON.stringify(a) === JSON.stringify(defaultPasswordConfig);
  };

  return (
    <>
      <Box sx={{ p: 3 }}>
        <Typography
          variant="h6"
          sx={{
            background: (theme: Theme) =>
              `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            fontWeight: "bold",
          }}
          gutterBottom
        >
          Password Rules Configuration
        </Typography>

        <TableContainer component={Paper} sx={{ mb: 3 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell sx={{ fontWeight: "bold", width: "30%" }}>
                  Rule
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", width: "20%" }}>
                  Enable/Disable
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", width: "50%" }}>
                  Value
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Minimum Length</TableCell>
                <TableCell>Required</TableCell>
                <TableCell>
                  <TextField
                    type="number"
                    size="small"
                    value={config.minLength.value}
                    onChange={(e) =>
                      handleChange("minLength", "value", Number(e.target.value))
                    }
                    inputProps={{ min: 6 }}
                    error={!!errors.minLength}
                    helperText={errors.minLength}
                    onKeyDown={(e) => {
                      if (e.key === ".") {
                        e.preventDefault();
                      }
                    }}
                  />
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell>Maximum Length</TableCell>
                <TableCell>Required</TableCell>
                <TableCell>
                  <TextField
                    type="number"
                    size="small"
                    value={config.maxLength.value}
                    onChange={(e) =>
                      handleChange("maxLength", "value", Number(e.target.value))
                    }
                    inputProps={{ min: config.minLength.value }}
                    error={!!errors.maxLength}
                    helperText={errors.maxLength}
                    onKeyDown={(e) => {
                      if (e.key === ".") {
                        e.preventDefault();
                      }
                    }}
                  />
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell>Uppercase Letters</TableCell>
                <TableCell>
                  <Switch
                    checked={config.requireUppercase.enabled}
                    onChange={(e) =>
                      handleChange(
                        "requireUppercase",
                        "enabled",
                        e.target.checked
                      )
                    }
                  />
                </TableCell>
                <TableCell>
                  {config.requireUppercase.enabled && (
                    <TextField
                      type="number"
                      size="small"
                      value={config.requireUppercase.value}
                      onChange={(e) =>
                        handleChange(
                          "requireUppercase",
                          "value",
                          Number(e.target.value)
                        )
                      }
                      inputProps={{ min: 1 }}
                      error={!!errors.requireUppercase}
                      helperText={errors.requireUppercase}
                      onKeyDown={(e) => {
                        if (e.key === ".") {
                          e.preventDefault();
                        }
                      }}
                    />
                  )}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell>Lowercase Letters</TableCell>
                <TableCell>
                  <Switch
                    checked={config.requireLowercase.enabled}
                    onChange={(e) =>
                      handleChange(
                        "requireLowercase",
                        "enabled",
                        e.target.checked
                      )
                    }
                  />
                </TableCell>
                <TableCell>
                  {config.requireLowercase.enabled && (
                    <TextField
                      type="number"
                      size="small"
                      value={config.requireLowercase.value}
                      onChange={(e) =>
                        handleChange(
                          "requireLowercase",
                          "value",
                          Number(e.target.value)
                        )
                      }
                      onKeyDown={(e) => {
                        if (e.key === ".") {
                          e.preventDefault();
                        }
                      }}
                      inputProps={{ min: 1 }}
                      error={!!errors.requireLowercase}
                      helperText={errors.requireLowercase}
                    />
                  )}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell>Special Characters</TableCell>
                <TableCell>
                  <Switch
                    checked={config.requireSpecialChars.enabled}
                    onChange={(e) =>
                      handleChange(
                        "requireSpecialChars",
                        "enabled",
                        e.target.checked
                      )
                    }
                  />
                </TableCell>
                <TableCell>
                  {config.requireSpecialChars.enabled && (
                    <Box
                      sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}
                    >
                      <TextField
                        type="text"
                        size="small"
                        value={config.requireSpecialChars.chars}
                        onChange={(e) =>
                          handleChange(
                            "requireSpecialChars",
                            "chars",
                            e.target.value
                          )
                        }
                        placeholder="Enter allowed special characters"
                        error={!!errors.requireSpecialChars?.chars}
                        helperText={errors.requireSpecialChars?.chars}
                        onKeyDown={(e) => {
                          if (e.key === " ") {
                            e.preventDefault();
                          }
                        }}
                      />
                      <TextField
                        type="number"
                        size="small"
                        value={config.requireSpecialChars.length}
                        onChange={(e) =>
                          handleChange(
                            "requireSpecialChars",
                            "length",
                            Number(e.target.value)
                          )
                        }
                        inputProps={{ min: 1 }}
                        placeholder="Length"
                        sx={{ width: "200px" }}
                        error={!!errors.requireSpecialChars?.length}
                        helperText={errors.requireSpecialChars?.length}
                        onKeyDown={(e) => {
                          if (e.key === ".") {
                            e.preventDefault();
                          }
                        }}
                      />
                    </Box>
                  )}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell>Numeric Characters</TableCell>
                <TableCell>
                  <Switch
                    checked={config.requireNumbers.enabled}
                    onChange={(e) =>
                      handleChange(
                        "requireNumbers",
                        "enabled",
                        e.target.checked
                      )
                    }
                  />
                </TableCell>
                <TableCell>
                  {config.requireNumbers.enabled && (
                    <TextField
                      type="number"
                      size="small"
                      value={config.requireNumbers.value}
                      onChange={(e) =>
                        handleChange(
                          "requireNumbers",
                          "value",
                          Number(e.target.value)
                        )
                      }
                      inputProps={{ min: 1 }}
                      error={!!errors.requireNumbers}
                      helperText={errors.requireNumbers}
                      onKeyDown={(e) => {
                        if (e.key === ".") {
                          e.preventDefault();
                        }
                      }}
                    />
                  )}
                </TableCell>
              </TableRow>

              {/* <TableRow>
              <TableCell>Prohibited Patterns</TableCell>
              <TableCell>
                <Switch
                  checked={config.prohibitedPatterns.enabled}
                  onChange={(e) =>
                    handleChange(
                      "prohibitedPatterns",
                      "enabled",
                      e.target.checked
                    )
                  }
                />
              </TableCell>
              <TableCell>
                {config.prohibitedPatterns.enabled && (
                  <Select
                    multiple
                    fullWidth
                    size="small"
                    value={config.prohibitedPatterns.values}
                    onChange={(e) =>
                      handleChange(
                        "prohibitedPatterns",
                        "values",
                        e.target.value as string[]
                      )
                    }
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip
                            key={value}
                            label={
                              prohibitedPatternOptions.find(
                                (opt) => opt.value === value
                              )?.label
                            }
                            size="small"
                          />
                        ))}
                      </Box>
                    )}
                  >
                    {prohibitedPatternOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              </TableCell>
            </TableRow> */}

              <TableRow>
                <TableCell>Password Expiry</TableCell>
                <TableCell>
                  <Switch
                    checked={config.passwordExpiry.enabled}
                    onChange={(e) =>
                      handleChange(
                        "passwordExpiry",
                        "enabled",
                        e.target.checked
                      )
                    }
                  />
                </TableCell>
                <TableCell>
                  {config.passwordExpiry.enabled && (
                    <TextField
                      type="number"
                      size="small"
                      value={config.passwordExpiry.value}
                      onChange={(e) => {
                        let val = e.target.value;

                        if (val.length > 1 && val.startsWith("0")) {
                          val = val.replace(/^0+/, "");
                        }

                        if (val.length > 3) {
                          val = val.slice(0, 3);
                        }

                        handleChange(
                          "passwordExpiry",
                          "value",
                          val === "" ? "" : Number(val)
                        );
                      }}
                      inputProps={{
                        min: 1,
                        max: 999,
                        maxLength: 3,
                      }}
                      InputProps={{
                        endAdornment:
                          config.passwordExpiry.value === 1 ? "day" : "days",
                      }}
                      error={!!errors.passwordExpiry}
                      helperText={errors.passwordExpiry}
                      onKeyDown={(e) => {
                        if (e.key === "." || e.key === "e" || e.key === "-") {
                          e.preventDefault();
                        }
                      }}
                    />
                  )}
                </TableCell>
              </TableRow>

              {/* <TableRow>
              <TableCell>Password History</TableCell>
              <TableCell>
                <Switch
                  checked={config.passwordHistory.enabled}
                  onChange={(e) =>
                    handleChange("passwordHistory", "enabled", e.target.checked)
                  }
                />
              </TableCell>
              <TableCell>
                {config.passwordHistory.enabled && (
                  <TextField
                    type="number"
                    size="small"
                    value={config.passwordHistory.value}
                    onChange={(e) =>
                      handleChange(
                        "passwordHistory",
                        "value",
                        Number(e.target.value)
                      )
                    }
                    InputProps={{ endAdornment: "previous passwords" }}
                  />
                )}
              </TableCell>
            </TableRow> */}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
          <Button
            variant="outlined"
            onClick={handleReset}
            disabled={isConfigDefault(config)}
            sx={{ borderRadius: "4px" }}
          >
            Reset to Defaults
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isConfigEqual(config, initialConfig) || !isFormValid()}
            sx={{ borderRadius: "4px" }}
          >
            Save Rules
          </Button>
        </Box>
      </Box>
      <MessageDialog
        open={messageDialogOpen}
        handleClose={handleMessageDialogClose}
      />
    </>
  );
};

export default PasswordConfiguration;
